import { useEffect, useRef, useCallback } from 'react';
import { connect } from 'react-redux';
import withMobileView from '@/hoc/withMobileView';
import { roundRating, isServer } from '@/helpers';
import pinStyles from './Markers.module.scss';
import ReactDOM from 'react-dom/client';
import Card from '@/components/elements/Card/Card';
import { withRouter } from 'react-router-dom';
import { placeService } from '@/services';
import React from 'react';

import { SearchPlace } from '@/types/api/services/search';
type PinStyles = {
  [key: string]: string | undefined;
};

const getPinSrc = (active: boolean, rating: number | false): string => {
  if (active) {
    return rating ? '/images/map-pin-selected.svg' : '/images/map-pin-selected-empty.svg';
  }

  return rating ? '/images/map-pin.svg' : '/images/map-pin-empty.svg';
};

const createPlacePopup = (
  map: any,
  maptilersdk: any,
  place: SearchPlace,
  isActive: boolean,
  getPlaceDetails: (place: SearchPlace) => React.ReactNode,
  pinStyles: PinStyles,
) => {
  const popupContainer = document.createElement('div');
  popupContainer.className = 'w-full';
  const reactRoot = ReactDOM.createRoot(popupContainer);
  reactRoot.render(React.createElement(Card, null, getPlaceDetails(place)));

  const popup = new maptilersdk.Popup({
    className: `overflow-hidden drop-shadow-sm rounded-md ${pinStyles.placePopup}`,
    offset: [0, isActive ? -70 : -50],
    focusAfterOpen: false,
    anchor: 'bottom',
    maxWidth: '300px',
  }).setDOMContent(popupContainer);

  popup.on('open', () => {
    const popupElement = popup.getElement();
    if (!popupElement) {
      return;
    }

    const mapContainer = map.getContainer();
    const mapBounds = mapContainer.getBoundingClientRect();
    const popupBounds = popupElement.getBoundingClientRect();

    let offsetX = 0;
    let offsetY = 0;
    const padding = 10;

    // Check if popup is out of view
    if (popupBounds.left < mapBounds.left + padding) {
      offsetX = popupBounds.left - (mapBounds.left + padding);
    } else if (popupBounds.right > mapBounds.right - padding) {
      offsetX = popupBounds.right - (mapBounds.right - padding);
    }
    if (popupBounds.top < mapBounds.top + padding) {
      offsetY = popupBounds.top - (mapBounds.top + padding);
    } else if (popupBounds.bottom > mapBounds.bottom - padding) {
      offsetY = popupBounds.bottom - (mapBounds.bottom - padding);
    }
    if (offsetX !== 0 || offsetY !== 0) {
      map.panBy([offsetX, offsetY], {
        duration: 500, // add a smooth animation
      });
    }
  });

  return { popup, reactRoot, container: popupContainer };
};

function Markers(props) {
  const {
    map,
    places,
    carouselIndex,
    setCarouselIndex,
    getPlaceDetails,
    isMobileView, // <-- Provided by withMobileView HOC
    placeHover, // <--- Redux state: ID of currently active marker
    search,
  } = props;

  // Access MapTiler SDK objects from the window or via the map instance
  const maptilersdk = window.maptilersdk || map?.getMapLibre();

  const markersRef = useRef([]);
  const prevPlaceIdsRef = useRef([]);
  const debugLayersRef = useRef([]);

  const createMarkerElement = (place: SearchPlace, isActive: boolean, rating: number | false, pinStyles: PinStyles) => {
    const iconSize = isActive ? [60, 67] : [40, 40];
    const el = document.createElement('span');
    const markerClassIsActiveName = isActive ? pinStyles?.placePinActive || 'place-pin-active' : '';
    const zIndexOffset = isActive ? '100' : '3';

    el.className = `place-pin ${markerClassIsActiveName}`;
    el.style.width = `${iconSize[0]}px`;
    el.style.height = `${iconSize[1]}px`;
    el.style.cursor = 'pointer';
    el.style.position = 'absolute'; // Required for MapTiler custom elements
    el.style.zIndex = zIndexOffset; // Z-index handled by popup z-index listener

    const img = document.createElement('img');
    img.className = pinStyles.placePinImage;
    img.src = getPinSrc(isActive, rating);
    img.alt = place.about?.name || 'Place Pin';
    el.appendChild(img);

    if (rating) {
      const ratingEl = document.createElement('span');
      ratingEl.className = pinStyles.placePinRating;
      ratingEl.textContent = String(rating);
      el.appendChild(ratingEl);
    }

    return el;
  };

  const displayPlaceDetails = useCallback(
    (place: SearchPlace, key: number) => {
      if (!isServer) {
        placeService.storeSerpImpressions([place.id]);

        if (isMobileView && setCarouselIndex) {
          setCarouselIndex(key);
        }
      }
    },
    [isMobileView, setCarouselIndex],
  );

  interface MarkerItem {
    marker: any;
    markerElement: HTMLElement | null;
    clickHandler: ((event: MouseEvent) => void) | null;
    reactRoot?: ReactDOM.Root;
    container?: HTMLElement;
    placeId: string | number;
    rating: number | false;
    isActive: boolean;
    popup?: any;
  }

  const removeMarker = useCallback((item: MarkerItem) => {
    if (item.markerElement && item.clickHandler) {
      // remove click event listener on mobile
      item.markerElement.removeEventListener('click', item.clickHandler);
    }
    if (item.popup && item.popup.isOpen()) {
      // remove popup if it's open
      item.popup.remove();
    }
    if (item.reactRoot) {
      setTimeout(() => {
        item.reactRoot!.unmount();
      }, 0);
    }
    if (item.marker && typeof item.marker.remove === 'function') {
      item.marker.remove();
    }
  }, []);

  const cleanupMarkers = useCallback(() => {
    markersRef.current.forEach((item: MarkerItem) => removeMarker(item));
    markersRef.current = [];

    // Clean up debug layers
    debugLayersRef.current.forEach((layerId) => {
      if (map && map.getLayer(layerId)) {
        map.removeLayer(layerId);
      }
      if (map && map.getSource(layerId)) {
        map.removeSource(layerId);
      }
    });
    debugLayersRef.current = [];
  }, [map, markersRef, removeMarker]);

  const addMarkersToMap = useCallback(() => {
    if (!map || !maptilersdk) {
      cleanupMarkers();
      return;
    }

    const currentPlaceIds = places.map((place: SearchPlace) => place.id);
    const prevPlaceIds = prevPlaceIdsRef.current; // Use previous IDs only for desktop view

    const placesToRemove = markersRef.current.filter((item) => !currentPlaceIds.includes(item.placeId));
    const placesToAdd = places.filter((place: SearchPlace) => !prevPlaceIds.includes(place.id));

    placesToRemove.forEach((item) => {
      removeMarker(item);
      markersRef.current = markersRef.current.filter((markerItem) => markerItem.placeId !== item.placeId);
    });

    placesToAdd.forEach((place: SearchPlace) => {
      if (place.contact?.position) {
        const placeIndex = places.findIndex((p) => p.id === place.id);
        const { lat, lon } = place.contact.position;
        const coordinates = [parseFloat(String(lon)), parseFloat(String(lat))];
        let isActive = false; // Markers are inactive by default when added. The useEffect will activate them.

        if (isMobileView) {
          // Mobile: ONLY carouselIndex dictates visual active marker
          isActive = carouselIndex === placeIndex;
        }

        const rating = place?.rating?.score ? roundRating(place.rating.score) : false;

        // Create the marker element using the helper function
        const el = createMarkerElement(place, isActive, rating, pinStyles);

        const popupDetails = createPlacePopup(map, maptilersdk, place, isActive, getPlaceDetails, pinStyles);
        const popup = popupDetails.popup;
        const reactRoot = popupDetails.reactRoot;
        const popupContainer = popupDetails.container;

        const clickHandler = () => {
          displayPlaceDetails(place, placeIndex);
        };
        el.addEventListener('click', clickHandler);

        const marker = new maptilersdk.Marker({
          element: el,
          anchor: 'bottom',
        }).setLngLat(coordinates);

        marker.setPopup(popup);

        marker.addTo(map);

        markersRef.current.push({
          marker: marker,
          markerElement: el, // Store the element reference
          clickHandler: clickHandler, // Store the handler reference
          reactRoot: reactRoot,
          container: popupContainer,
          placeId: place.id, // Store place ID
          rating: rating, // Store place rating
          isActive: isActive, // Store initial active state
        });
      }
    });

    // --- Add Debug Locations (Polygons, Circles, Rectangles) ---
    if (search?.debugLocation?.length > 0) {
      search.debugLocation?.forEach((locationGroup, groupIndex) => {
        locationGroup.forEach((loc, locIndex) => {
          const id = `debug-location-${groupIndex}-${locIndex}`;
          const color = loc?.must_not ? 'red' : 'green';

          if (loc.type === 'polygon') {
            const polygonCoordinates = [
              loc.coordinates[0].map((coord) => [parseFloat(coord[1]), parseFloat(coord[0])]),
            ];
            if (!map.getSource(id)) {
              // Only add source if it doesn't exist
              map.addSource(id, {
                type: 'geojson',
                data: {
                  type: 'Feature',
                  geometry: {
                    type: 'Polygon',
                    coordinates: polygonCoordinates,
                  },
                },
              });
            }
            if (!map.getLayer(id)) {
              // Only add layer if it doesn't exist
              map.addLayer({
                id: id,
                type: 'fill',
                source: id,
                paint: {
                  'fill-color': color,
                  'fill-opacity': 0.2,
                  'fill-outline-color': color,
                },
              });
            }
          } else if (loc.type === 'geo_distance') {
            // For circles, MapTiler SDK uses a 'circle' layer with a GeoJSON Point source.
            // Radius is set via 'circle-radius' paint property
            const centerCoordinates = [parseFloat(loc.position[1]), parseFloat(loc.position[0])]; // [lon, lat]

            if (!map.getSource(id)) {
              map.addSource(id, {
                type: 'geojson',
                data: {
                  type: 'Feature',
                  geometry: {
                    type: 'Point',
                    coordinates: centerCoordinates,
                  },
                  properties: {
                    radius: loc.distance,
                  },
                },
              });
            }

            if (!map.getLayer(id)) {
              map.addLayer({
                id: id,
                type: 'circle',
                source: id,
                paint: {
                  'circle-radius': ['get', 'radius'], // Get radius from feature properties (in meters if using projection)
                  'circle-color': color,
                  'circle-opacity': 0.2,
                  'circle-stroke-color': color,
                  'circle-stroke-width': 1,
                },
              });
            }
            debugLayersRef.current.push(id);
          } else if (loc.type === 'rectangle') {
            // For rectangles, create a GeoJSON Polygon from bounds.
            // `loc.position` is `[[southLat, westLng], [northLat, eastLng]]` (Leaflet-style bounds).
            // Convert to GeoJSON Polygon expected `[[westLng, southLat], [eastLng, southLat], [eastLng, northLat], [westLng, northLat], [westLng, southLat]]`
            const southWest = loc.position[0]; // [southLat, westLng]
            const northEast = loc.position[1]; // [northLat, eastLng]

            const rectangleCoordinates = [
              [parseFloat(southWest[1]), parseFloat(southWest[0])], // westLng, southLat
              [parseFloat(northEast[1]), parseFloat(southWest[0])], // eastLng, southLat
              [parseFloat(northEast[1]), parseFloat(northEast[0])], // eastLng, northLat
              [parseFloat(southWest[1]), parseFloat(northEast[0])], // westLng, northLat
              [parseFloat(southWest[1]), parseFloat(southWest[0])], // westLng, southLat (close the loop)
            ];

            if (!map.getSource(id)) {
              map.addSource(id, {
                type: 'geojson',
                data: {
                  type: 'Feature',
                  geometry: {
                    type: 'Polygon',
                    coordinates: [rectangleCoordinates],
                  },
                },
              });
            }

            if (!map.getLayer(id)) {
              map.addLayer({
                id: id,
                type: 'fill',
                source: id,
                paint: {
                  'fill-color': color,
                  'fill-opacity': 0.2,
                  'fill-outline-color': color,
                },
              });
            }
            debugLayersRef.current.push(id);
          }
        });
      });
    }

    prevPlaceIdsRef.current = currentPlaceIds;

    return () => {
      cleanupMarkers();
    };
  }, [map, maptilersdk, isMobileView, getPlaceDetails, search]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Manage popup visibility based on placeHover or carouselIndex
  useEffect(() => {
    const currentMarkersById = new Map();
    markersRef.current.forEach((markerItem) => {
      currentMarkersById.set(markerItem.placeId, markerItem);
    });

    places.forEach((place: SearchPlace, placeIndex: number) => {
      const item = currentMarkersById.get(place.id);
      if (item) {
        let isActive: boolean;
        if (isMobileView) {
          isActive = carouselIndex === placeIndex;
        } else {
          isActive =
            placeHover !== undefined &&
            place.id !== undefined &&
            parseInt(placeHover, 10) === parseInt(String(place.id), 10);
        }

        item.isActive = isActive;

        const zIndexOffset = isActive ? '100' : '3';

        // Update marker element class and image
        if (item.marker && item.marker.getElement) {
          const el = item.marker.getElement();

          if (el) {
            el.classList.remove(pinStyles.placePinActive || 'place-pin-active');
            if (isActive) {
              el.classList.add(pinStyles.placePinActive || 'place-pin-active');
            }

            const iconSize = isActive ? [60, 67] : [40, 40];
            el.style.width = `${iconSize[0]}px`;
            el.style.height = `${iconSize[1]}px`;
            el.style.zIndex = zIndexOffset;

            const img = el.querySelector('img');
            if (img) {
              img.src = getPinSrc(isActive, item.rating);
            }

            if (isMobileView) {
              el.removeEventListener('click', item.clickHandler);
              const clickHandler = () => {
                displayPlaceDetails({ id: item.placeId }, placeIndex);
              };
              el.addEventListener('click', clickHandler);
              item.clickHandler = clickHandler;

              // Ensure popup is closed/removed for mobile
              if (item.popup && item.popup.isOpen()) {
                item.popup.remove();
              }
              // Disconnect popup from marker so it doesn't open on click
              if (item.marker && item.marker.getPopup()) {
                item.marker.setPopup(null);
              }
            } else {
              if (item.markerElement && item.clickHandler) {
                // remove if mobile click handler was attached for Desktop
                item.markerElement.removeEventListener('click', item.clickHandler);
                item.clickHandler = null;
              }

              // Ensure popup is set on the marker (MapTiler SDK handles open/close on marker click)
              if (item.marker && item.popup && !item.marker.getPopup()) {
                item.marker.setPopup(item.popup);
              }
            }
          }
        }
      }
    });
  }, [isMobileView, carouselIndex, placeHover, places, displayPlaceDetails]);

  useEffect(() => {
    addMarkersToMap();
  }, [addMarkersToMap]);

  useEffect(() => {
    return () => {
      cleanupMarkers(); // This ensures all markers and debug layers are removed when component unmounts
    };
  }, [cleanupMarkers]);

  return null;
}

function mapStateToProps(state) {
  const { placeHover } = state.place;
  const { searchV2: search } = state;

  return { placeHover, search };
}

const MarkersConnected = withRouter(withMobileView(connect(mapStateToProps)(Markers)));

export { MarkersConnected as MapTilerMarkers };
